/**
 * React Query hooks for Menus (assistant, week, summary)
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getAssistant,
  getWeekMenu,
  setMenuEntry,
  removeMenuEntry,
  getWeekSummary,
} from '@/lib/api/menus';
import type { AssistantQuery, SetMenuEntryRequest } from '@/types/api';
import { menuKeys } from '@/lib/query-keys';

export { menuKeys };

export function useAssistant(query: AssistantQuery, enabled = true) {
  return useQuery({
    queryKey: menuKeys.assistant(query),
    queryFn: () => getAssistant(query),
    enabled,
  });
}

export function useWeekMenu(weekStart: string) {
  return useQuery({
    queryKey: menuKeys.week(weekStart),
    queryFn: () => getWeekMenu(weekStart),
    enabled: Boolean(weekStart),
  });
}

export function useWeekSummary(weekStart: string) {
  return useQuery({
    queryKey: menuKeys.summary(weekStart),
    queryFn: () => getWeekSummary(weekStart),
    enabled: Boolean(weekStart),
  });
}

export function useSetMenuEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SetMenuEntryRequest) => setMenuEntry(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: menuKeys.all });
    },
  });
}

export function useRemoveMenuEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      date,
      mealType,
    }: {
      date: string;
      mealType: 'lunch' | 'dinner';
    }) => removeMenuEntry(date, mealType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: menuKeys.all });
    },
  });
}
